import { Checkbox, TableBody, TableCell, TableRow } from '@client/toolkit';
import React from 'react';
import { ExpanableTableWraper, SortDirection } from '../../components/expanable-table-wraper';
import { TableExpandableCell } from '../../components/table-expanable-cell';
import { VatPercentageByBuildingDto, VatPercentageTenantDto } from '@client/api';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, Link } from 'react-router-dom';
import { PortfolioPaths } from '../../../portfolio/portfolio.router';
import { MdOpenInNew } from 'react-icons/md';
import { AgreementsPaths } from '../../../agreements/agreements.router';
import { VatVarianceDisplay } from '../../components/vat-variance-display';

export const VatPercentageTable = ({
  loading,
  data,
  baseYear,
  comparisonYear,
  onSelected,
  onSort,
  sortColumn,
  sortDirection,
}: {
  loading?: boolean;
  data: VatPercentageByBuildingDto[];
  baseYear: number;
  comparisonYear: number;
  onSelected?: (ids: Set<string>) => void;
  onSort?: (col: string, direction: SortDirection) => void;
  sortColumn?: string;
  sortDirection?: SortDirection;
}) => {
  const { t } = useTranslation('reporting');
  const navigate = useNavigate();
  const columns = [
    { key: 'buildingName', label: t('vatPercentage.building'), isSortable: true, className: 'w-[20%]' },
    { key: 'tenantsAmount', label: t('vatPercentage.tenants'), className: 'w-[15%]' },
    { key: 'baseYear', label: t('vatPercentage.vatPercentageYear', { year: baseYear }), className: 'w-[15%]' },
    { key: 'comparisonYear', label: t('vatPercentage.vatPercentageYear', { year: comparisonYear }), className: 'w-[15%]' },
    { key: 'agreementName', label: t('vatPercentage.agreement'), className: 'w-[20%]' },
    { key: 'lastUpdated', label: t('vatPercentage.updated'), className: 'w-[10%]' },
    { key: 'trend', label: t('vatPercentage.trend'), className: 'w-[15%]' },
  ];

  const formatVatPercentage = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    return `${value}%`;
  };

  return (
    <ExpanableTableWraper
      loading={loading}
      onSort={onSort}
      onSelected={onSelected}
      columns={columns}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      data={data}
      renderBody={(
        data,
        selectedRows,
        expandedRows,
        handleToggleSelect,
        handleKeyDownSelect,
        handleToggleExpand,
        handleKeyDownExpand,
      ) => (
        <TableBody>
          {data.map((building) => (
            <React.Fragment key={building.buildingId}>
              <TableRow
                className="bg-surface-elevated/40 border-b border-surface-elevated group hover:bg-surface-elevated"
                onClick={() =>
                  navigate(generatePath(PortfolioPaths.BuildingAgreements, { buildingId: building.buildingId }))
                }
              >
                <TableCell className="px-2 py-2 align-top content-center">
                  <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    checked={selectedRows.has(building.buildingId)}
                    onChange={() => handleToggleSelect(building.buildingId)}
                    tabIndex={0}
                    onKeyDown={(e) => handleKeyDownSelect(e, building.buildingId)}
                  />
                </TableCell>
                <TableExpandableCell
                  label={building.buildingName}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleExpand(building.buildingId);
                  }}
                  onKeyDown={(e) => handleKeyDownExpand(e, building.buildingId)}
                  isExpanded={expandedRows.has(building.buildingId)}
                  icon={<MdOpenInNew className="text-brand-primary" />}
                />
                <TableCell className="px-2 py-2 align-top">
                  {t('table.numberOfTenants', { count: building.numberOfTenants ?? 0 })}
                </TableCell>
                <TableCell className="px-2 py-2 align-top">
                  {formatVatPercentage(building.currentPeriodAverageVat)}
                </TableCell>
                <TableCell className="px-2 py-2 align-top">
                  {formatVatPercentage(building.comparisonPeriodAverageVat)}
                </TableCell>
                <TableCell className="px-2 py-2 align-top">-</TableCell>
                <TableCell className="px-2 py-2 align-top">-</TableCell>
                <TableCell className="px-2 py-2 align-top">
                  <VatVarianceDisplay variance={building.variance} />
                </TableCell>
              </TableRow>
              {expandedRows.has(building.buildingId) &&
                building.tenants?.map((tenant: VatPercentageTenantDto) => (
                  <React.Fragment key={`${tenant.agreementId}`}>
                    <TableRow className="bg-surface-base border-b border-surface-elevated">
                      <TableCell className="px-2 py-2"></TableCell>
                      <TableCell className="px-2 py-2"></TableCell>
                      <TableCell className="px-2 py-2 align-top">{tenant.tenantName}</TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        {formatVatPercentage(tenant.currentPeriodVatPercentage)}
                      </TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        {formatVatPercentage(tenant.comparisonPeriodVatPercentage)}
                      </TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        <Link
                          to={generatePath(AgreementsPaths.Agreement, { id: tenant.agreementId })}
                          className="text-brand-primary hover:underline"
                        >
                          {tenant.agreementName}
                        </Link>
                      </TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        {tenant.lastUpdated ? new Date(tenant.lastUpdated).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="px-2 py-2 align-top">
                        <VatVarianceDisplay variance={tenant.variance} />
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
            </React.Fragment>
          ))}
        </TableBody>
      )}
    />
  );
};
